/**
 * Who did this PM, and on which day — TRBUD-459.
 *
 * The one form for a PM bonus credit, used in two places: the prompt that
 * appears after a PM work order reaches a completed stage, and the PM bonus
 * card on the work order's Details tab (and its correction from Reports).
 *
 * ## A suggestion, never an answer
 *
 * The form opens with the mechanic the work records point to (only when
 * exactly one person has work marks or logged time on the job) and the last
 * day they show work, else today. It says so beside each field. Nothing is
 * saved until a person presses Save: who pressed Repair complete is never the
 * answer, and neither is a stage-entry or paperwork date.
 *
 * ## Completion is never held up
 *
 * The prompt appears AFTER the move has been recorded. "Not now" closes it and
 * the PM waits in the report's "Needs a credit" list.
 *
 * ## Why the prompt lives in a host, not in the stage control
 *
 * The queue row that moved a job to Repair complete can vanish from the list
 * the moment the job completes, and a dialog inside it would vanish too. So
 * `offerPmCredit` hands the work order to `PmCreditPromptHost`, mounted once in
 * the app shell, which outlives the row.
 */
import { useEffect, useId, useState, useSyncExternalStore } from "react";
import { pmCreditEligibilityNotes, type PmCreditPerson, type PmCreditState, type StageRef } from "@truckbuddy/contract";
import { useAppSession } from "../lib/shell";
import { usePmCredit, useSetPmCredit } from "../lib/pm-bonus-queries";
import { DateField } from "./controls";
import { Dialog, DialogError } from "./Dialog";

/** "Sam Same (sam.b@…)" only when the name alone would not tell two people apart. */
export function personLabel(person: Pick<PmCreditPerson, "displayName" | "email" | "active">, all: readonly Pick<PmCreditPerson, "displayName">[]): string {
  const twins = all.filter((other) => other.displayName === person.displayName).length > 1;
  return `${person.displayName}${twins ? ` (${person.email})` : ""}${person.active ? "" : " · no longer active"}`;
}

export function PmCreditDialog({
  workOrderId,
  state,
  subject,
  prompt = false,
  onClose,
}: {
  workOrderId: string;
  state: PmCreditState;
  /** "WO-1412", for the title. */
  subject?: string;
  /** Opened by the completion prompt: the dismiss button says "Not now". */
  prompt?: boolean;
  onClose: () => void;
}) {
  const save = useSetPmCredit(workOrderId);
  const ids = { mechanic: useId(), day: useId(), reason: useId() };
  const correcting = state.credit !== null;
  const own = state.canRecord === "own";
  const people = state.people;
  const [mechanic, setMechanic] = useState(
    state.credit?.mechanicUserId ?? (own ? people[0]?.userId ?? "" : state.suggestion.mechanicUserId ?? ""),
  );
  const [day, setDay] = useState(state.credit?.performedOn ?? state.suggestion.performedOn);
  const [reason, setReason] = useState("");
  const changed = !correcting || mechanic !== state.credit!.mechanicUserId || day !== state.credit!.performedOn;
  const dayOk = /^\d{4}-\d{2}-\d{2}$/.test(day) && day <= state.today;
  const ready = mechanic !== "" && dayOk && changed && (!correcting || reason.trim().length > 0) && !save.isPending;
  const suggested = !correcting && state.suggestion.mechanicUserId !== null && mechanic === state.suggestion.mechanicUserId;
  const several = state.suggestion.candidates.length > 1;

  function submit() {
    save.mutate(
      {
        mechanicUserId: mechanic,
        performedOn: day,
        expectedVersion: state.credit?.version ?? null,
        reason: correcting ? reason.trim() : null,
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <Dialog
      title={`${correcting ? "Correct the PM bonus credit" : "Who did this PM?"}${subject ? ` · ${subject}` : ""}`}
      onClose={onClose}
      width={520}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {prompt ? "Not now" : "Cancel"}
          </button>
          <button type="button" className="btn btn-primary" disabled={!ready} onClick={submit}>
            {save.isPending ? "Saving…" : correcting ? "Save correction" : "Save"}
          </button>
        </>
      }
    >
      <p style={{ margin: "0 0 var(--space-3)" }}>
        The mechanic recorded here earns this PM&rsquo;s bonus. One mechanic per PM.
        {prompt ? " The work order is already complete; you can also do this later from its Details tab." : ""}
      </p>
      <div className="field">
        <label htmlFor={ids.mechanic}>Mechanic who did the PM</label>
        {own ? (
          <p id={ids.mechanic} style={{ margin: 0 }}><strong>{people[0]?.displayName ?? "You"}</strong> (you)</p>
        ) : (
          <select id={ids.mechanic} className="input" value={mechanic} onChange={(event) => setMechanic(event.target.value)}>
            <option value="">Choose a person</option>
            {people.map((person) => (
              <option key={person.userId} value={person.userId}>
                {personLabel(person, people)}
                {person.isMechanic ? "" : " · not a mechanic"}
              </option>
            ))}
          </select>
        )}
        <div className="text-muted" style={{ fontSize: "var(--tb-text-xs)", marginTop: 3 }}>
          {suggested
            ? "Suggested from the work records (work marks and logged time). Check it before saving."
            : several
              ? `Several people logged work on this job: ${state.suggestion.candidates
                  .map((candidate) => `${candidate.displayName} (last on ${candidate.lastWorkedOn})`)
                  .join(", ")}. Choose who did the PM.`
              : own || correcting
                ? ""
                : "No work records name anyone. Choose who did the PM."}
        </div>
      </div>
      <div className="field">
        <label htmlFor={ids.day}>Day the PM was done</label>
        <DateField id={ids.day} value={day} onChange={setDay} purpose="recorded" />
        <div className="text-muted" style={{ fontSize: "var(--tb-text-xs)", marginTop: 3 }}>
          {correcting
            ? "The PM counts in the pay period this day falls in."
            : state.suggestion.dateBasis === "work_records"
              ? "Suggested: the last day the work records show work. Change it if the PM was done on another day."
              : "Today, as a suggestion. Change it if the PM was done on another day."}
        </div>
      </div>
      {correcting ? (
        <div className="field">
          <label htmlFor={ids.reason}>Why is this being corrected?</label>
          <textarea id={ids.reason} className="input" rows={2} maxLength={500} value={reason}
            onChange={(event) => setReason(event.target.value)} />
        </div>
      ) : null}
      <DialogError error={save.error as Error | null} />
    </Dialog>
  );
}

/* ── the completion prompt ───────────────────────────────────────── */

/** The work order offered, and when: only a read taken after the move counts. */
let pending: { workOrderId: string; at: number } | null = null;
const listeners = new Set<() => void>();
function emit() {
  for (const listener of listeners) listener();
}

/**
 * Called by the stage control after a move is recorded. Only a move into a
 * completed, non-cancelled stage offers the prompt; the host then decides
 * whether this work order is a PM that still needs a credit.
 */
export function offerPmCredit(workOrderId: string, stage: Pick<StageRef, "isCompleted" | "code"> | undefined): void {
  // A move answer with no stage offers nothing, rather than breaking the move's own success path.
  if (!stage?.isCompleted || stage.code === "cancelled") return;
  pending = { workOrderId, at: Date.now() };
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Mounted once, in the app shell. */
export function PmCreditPromptHost() {
  const { can } = useAppSession();
  const offer = useSyncExternalStore(subscribe, () => pending, () => null);
  const workOrderId = offer?.workOrderId ?? null;
  const allowed = can.correctPmCredit || can.confirmOwnPmCredit;
  const credit = usePmCredit(workOrderId ?? "", allowed && workOrderId !== null);
  const [shown, setShown] = useState<string | null>(null);

  // The offer is module state; it belongs to this host, so it goes with it (on
  // sign-out, or between tests that each mount their own shell).
  useEffect(() => () => {
    pending = null;
  }, []);

  useEffect(() => {
    if (!offer || !credit.data || credit.data.workOrderId !== offer.workOrderId) return;
    // The card on the same page may hold a read from BEFORE the move ("not
    // complete yet"). Acting on it would dismiss the prompt for a PM that has
    // just been completed, so wait for a read taken after the offer.
    if (credit.isFetching || credit.dataUpdatedAt < offer.at) return;
    const state = credit.data;
    if (state.eligibility === "eligible" && state.credit === null && state.canRecord !== null) {
      setShown(offer.workOrderId);
    } else {
      pending = null;
      emit();
    }
  }, [offer, credit.data, credit.isFetching, credit.dataUpdatedAt]);

  if (!allowed || shown === null || !credit.data || credit.data.workOrderId !== shown) return null;
  return (
    <PmCreditDialog
      workOrderId={shown}
      state={credit.data}
      prompt
      onClose={() => {
        setShown(null);
        pending = null;
        emit();
      }}
    />
  );
}

/** The sentence for a work order that does not earn a bonus. Re-exported for the card. */
export function eligibilityNote(state: PmCreditState): string {
  return pmCreditEligibilityNotes[state.eligibility];
}
